import * as React from 'react'
import { IsMobileContext } from '../../common/Context'
import { InputProps, InputState, ActionTypes, InputActionTypes } from './types'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import { ValidationService } from '../../common/ValidationService'
import Check from '../../images/check.svg'
import SmallX from '../../images/x_small.svg'
import './style.scss'

const Input: React.FC<InputProps> = ({
  field,
  placeholder,
  required,
  email,
  phone,
  errorText,
  onChange,
  className,
  revalidate
}) => {
  const {t} = useTranslation()
  const { isMobile } = React.useContext(IsMobileContext)

  const inputInitialState: InputState = {
    value: '',
    isValid: !required,
    touched: false,
  }

  const inputReducer = (state: InputState, action: InputActionTypes) => {
    switch (action.type) {
    case ActionTypes.INPUT_CHANGE:
      return {
        ...state,
        value: action.value,
        isValid: action.isValid,
      }
    case ActionTypes.INPUT_BLUR:
      return {
        ...state,
        touched: true,
      }
    default:
      return state
    }
  }

  const [inputState, dispatch] = React.useReducer(inputReducer, inputInitialState)
  const [bgClass, setBgClass] = React.useState('bgBlurred')

  React.useEffect(() => {
    if (revalidate) {
      dispatch({ type: ActionTypes.INPUT_BLUR })
    }
  }, [revalidate])

  React.useEffect(() => {
    if (inputState.touched) {
      onChange(field, inputState.value, inputState.isValid)
    }
  }, [inputState, onChange, field])

  const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const textInput = event.target.value
    const validationRules = [
      required && ((value: string) => value.trim().length > 0),
      email && ((value: string) => ValidationService.validateEmail(value)),
      phone && ((value: string) => ValidationService.validatePhone(value)),
    ]
      .filter(Boolean) as ((value: string) => boolean)[]
    const isValid = validationRules.every(rule => rule(textInput))

    dispatch({ type: ActionTypes.INPUT_CHANGE, value: textInput, isValid })
  }

  const focusHandler = () => {
    setBgClass('bgFocused')
  }

  const blurHandler = () => {
    if ((inputState.value.trim().length === 0 && inputState.isValid) || !inputState.isValid) {
      setBgClass('bgBlurred')
    }
    dispatch({ type: ActionTypes.INPUT_BLUR })
  }

  const iconSize = isMobile ? '4vw' : '1vw'
  const validatedOk = inputState.value.trim().length > 0 && inputState.isValid && inputState.touched
  const validatedError = !inputState.isValid && inputState.touched

  return (
    <div className={`Input ${className || ''}`}>
      <div 
        className={`FontS w-full
          ${bgClass} hover:bgFocused
          flex items-center
          ${validatedError ? 'border-b border-accent -mb-px' : ''}`}
        style={{padding: iconSize}}
      >
        <div className="flex-grow relative">
          <input
            type="text"
            className="focus:outline-none bg-transparent w-full"
            value={inputState.value}
            required={required}
            onChange={changeHandler}
            onFocus={focusHandler}
            onBlur={blurHandler}
          />
          {inputState.value.length === 0 ? (
            <div className="absolute top-0 pointer-events-none">
              {placeholder}
              {!required && <span className="text-gray-50"> ({t('common:optional')})</span>}
            </div>
          ) : null}
        </div>
        {validatedError && errorText && (
          <div className="flex-grow-0 text-accent">
            &nbsp;{errorText}
          </div>
        )}
        {validatedError && (
          <SmallX style={{width: iconSize, height: iconSize, marginLeft: iconSize}} />
        )}
        {validatedOk && (
          <Check style={{width: iconSize, height: iconSize, marginLeft: iconSize}} />
        )}
      </div>
    </div>
  )
}

export default Input
